import { FC, useEffect, useMemo } from 'react';
import confetti from 'canvas-confetti';

import { AppModalProps } from '@/types/modal.type';
import { ModalView } from '@/Components/ModalView';
import { Button } from '@/Components/Button';
import { FlickAnimation } from '@/Components/FlickAnimation';
import { CherrySvg } from '@/Components/Icons/CherrySvg';
import { useAppModal } from '@/provider/AppModal/AppModal.context';
import { MODAL_QUERY_ARG } from '@/types/query.type';

type TyForUpgradeModalProps = AppModalProps<{}>;

export const TyForUpgradeModal: FC<TyForUpgradeModalProps> = ({ onClose }) => {
    const { dialogElementRef } = useAppModal();

    useEffect(() => {
        const dialog = dialogElementRef?.current;

        if (!dialog) {
            return;
        }

        const canvas = document.createElement('canvas');

        canvas.style.cssText =
            'position:absolute;inset:0;width:100%;height:100%;pointer-events:none';

        // Insert between the backdrop div (first child) and the content box (last child)
        // so DOM order places it above the backdrop but below the modal card
        // within the dialog's top-layer stacking context.
        dialog.insertBefore(canvas, dialog.lastElementChild);

        const BRAND_COLORS = [
            '#ffffff', // white
            '#381c70', // purple (ocean.500)
            '#10b5a8', // teal   (brand.teal)
            '#e46447', // red    (brand.red)
            '#f99e00', // yellow (brand.yellow)
            '#175773', // blue   (brand.blue)
        ];

        const shoot = confetti.create(canvas, { resize: true });
        const count = 200;
        const defaults = { origin: { y: 0.7 }, colors: BRAND_COLORS };

        const fire = (particleRatio: number, opts: confetti.Options) => {
            shoot({
                ...defaults,
                ...opts,
                particleCount: Math.floor(count * particleRatio),
            });
        };

        fire(0.25, { spread: 26, startVelocity: 55 });
        fire(0.2, { spread: 60 });
        fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
        fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
        fire(0.1, { spread: 120, startVelocity: 45 });

        return () => {
            canvas.remove();
        };
    }, [dialogElementRef?.current]);

    const currentUrlWithoutModal = useMemo(() => {
        try {
            const nextUrl = new URL(window.location.href);

            if (nextUrl.searchParams.has(MODAL_QUERY_ARG)) {
                nextUrl.searchParams.delete(MODAL_QUERY_ARG);
            }

            return nextUrl.pathname + nextUrl.search;
        } catch {
            return '/';
        }
    }, []);

    return (
        <ModalView
            maxWidth="sm"
            onClose={onClose}
            contentContainerClassName="py-6 px-8 text-brand-ocean flex flex-col items-center text-center">
            <div className="relative min-w-60 h-24 flex items-center justify-center">
                <FlickAnimation className="absolute z-10 top-0 left-12" />
                <div className="absolute bottom-4 right-20">
                    <CherrySvg className="w-12 h-12" />
                </div>
            </div>
            <div className="text-heading-four font-bold text-brand-ocean mb-2">
                Thanks for choosing us!
            </div>
            <div className="text-body-text-15 text-brand-ocean">
                We're delighted to have you as a customer.
            </div>
            <div className="text-body-text-15 text-brand-ocean mt-2 mb-6">
                — The ReelFlow Team
            </div>
            <Button
                elementType="a"
                className="w-full h-9 flex justify-center items-center"
                variant="primary"
                href={currentUrlWithoutModal}>
                Get started!
            </Button>
        </ModalView>
    );
};
